import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import {
  NgbDatepickerConfig,
  NgbDropdownConfig,
  NgbInputDatepickerConfig,
  NgbModal,
  NgbModalConfig,
  NgbPopoverConfig,
  NgbTimepickerConfig,
  NgbTooltipConfig
} from '@ng-bootstrap/ng-bootstrap';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { interval } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { AuthService } from './core/services/auth.service';
import { ConnectionCheckService } from './core/services/connection-check.service';
import { GlobalModalService } from './core/services/global-modal.service';
import { SideNavService } from './core/services/side-nav.service';

@Component({
  selector: 'th-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    NgbDropdownConfig,
    NgbDatepickerConfig,
    NgbTimepickerConfig,
    NgbModal,
    NgbModalConfig,
    NgbTooltipConfig,
    NgbPopoverConfig
  ]
})
export class AppComponent implements OnInit {
  @ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef<HTMLDivElement>;
  lazyLoading = false;

  connectionCheckToast: ActiveToast<unknown>;

  constructor(
    public authService: AuthService,
    private router: Router,
    private globalModalService: GlobalModalService,
    private connectionCheckService: ConnectionCheckService,
    private toastr: ToastrService,
    private environmentService: EnvironmentService,
    public sideNavService: SideNavService,
    dropdownConfig: NgbDropdownConfig,
    datepickerConfig: NgbDatepickerConfig,
    inputDatepickerConfig: NgbInputDatepickerConfig,
    timepickerConfig: NgbTimepickerConfig,
    modalConfig: NgbModalConfig,
    tooltipConfig: NgbTooltipConfig,
    popoverConfig: NgbPopoverConfig
  ) {
    this.customizeDropdownConfig(dropdownConfig);
    this.customizeDatepickerConfig(datepickerConfig);
    this.customizeDatepickerConfig(inputDatepickerConfig);
    this.customizeTimepickerConfig(timepickerConfig);
    this.customizeModalConfig(modalConfig);
    this.customizeTooltipConfig(tooltipConfig);
    this.customizePopoverConfig(popoverConfig);
  }

  ngOnInit() {
    this.startRouteChangeListener();
    this.startInternetConnectionCheck();
  }

  @HostListener('window:focus')
  onDocumentFocus() {
    if (this.authService.isAuthenticated()) {
      this.authService.isOrganizationChanged(this.authService.orgLevel).subscribe((isOrganizationChanged) => {
        if (isOrganizationChanged == null) {
          // We know the session is expired if the service call returns a null result
          this.authService.expireSession();
        } else if (isOrganizationChanged === true) {
          this.showOrganizationChangedModal();
        }
      });
    }
  }

  private customizeDropdownConfig(config: NgbDropdownConfig) {
    config.autoClose = true;
    config.container = 'body';
    config.placement = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];
  }

  private customizeDatepickerConfig(config: NgbDatepickerConfig | NgbInputDatepickerConfig) {
    config.minDate = {
      year: 1901,
      month: 1,
      day: 1
    };

    config.maxDate = (() => {
      const now = new Date();

      return {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate()
      };
    })();

    config.firstDayOfWeek = 7;
    config.outsideDays = 'collapsed';
    config.weekdays = 'short';
  }

  private customizeTimepickerConfig(config: NgbTimepickerConfig) {
    config.meridian = true;
    config.spinners = false;
    config.size = 'small';
  }

  private customizeModalConfig(config: NgbModalConfig) {
    config.backdrop = 'static';
    config.windowClass = 'animated fastest fadeInDown';
  }

  private customizeTooltipConfig(config: NgbTooltipConfig) {
    config.autoClose = true;
    config.closeDelay = 0;
    config.container = 'body';
    config.disableTooltip = false;
    config.openDelay = 0;
    config.placement = 'auto';
    config.tooltipClass = '';
    config.triggers = 'hover focus';
    config.animation = false;
  }

  private customizePopoverConfig(config: NgbPopoverConfig) {
    config.autoClose = 'outside';
    config.placement = 'auto';
    config.triggers = 'click';
    config.container = 'body';
    config.disablePopover = false;
    config.openDelay = 0;
    config.closeDelay = 0;
    config.animation = false;
  }

  private startRouteChangeListener() {
    this.router.events
      .pipe(
        filter((event) => {
          return (
            event instanceof NavigationEnd ||
            event instanceof RouteConfigLoadStart ||
            event instanceof RouteConfigLoadEnd
          );
        })
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          // Scroll to the top of the page every time navigation happens
          this.scrollContainer.nativeElement.scrollTo(0, 0);
        } else if (event instanceof RouteConfigLoadStart) {
          this.lazyLoading = true;
        } else if (event instanceof RouteConfigLoadEnd) {
          this.lazyLoading = false;
        }
      });
  }

  private startInternetConnectionCheck() {
    interval(5000).subscribe(() => {
      if (this.environmentService.enableInternetConnectionCheck) {
        this.connectionCheckService.hasInternetConnection().subscribe((hasConnection) => {
          if (hasConnection && this.connectionCheckToast) {
            this.connectionCheckToast.toastRef.close();
            this.connectionCheckToast = null;
          } else if (!hasConnection && !this.connectionCheckToast) {
            this.connectionCheckToast = this.toastr.error('Your device has lost its internet connection', undefined, {
              disableTimeOut: true,
              closeButton: false,
              tapToDismiss: false
            });
          }
        });
      }
    });
  }

  private showOrganizationChangedModal() {
    this.globalModalService
      .alert(
        {
          bodyText:
            'You have switched to a new organization in another tab.<br/>' +
            'You will now be redirected to your dashboard.',
          showCloseButton: false
        },
        {
          backdrop: 'static',
          keyboard: false
        }
      )
      .then(() => {
        // Switching organizations sets a new login response in the browser's localStorage.
        // We need to update the current tab's in-memory login response object with
        // the latest value from localStorage.
        this.authService.refreshLoginResponse();

        // Redirecting to /login to get the authGuard to run again since LoginComponent
        // does its own redirect to /private if you're already logged in. This solves
        // issues when switching between accounts and one hasn't agreed to the T&C yet.
        this.router.navigate(['/login']);
      });
  }
}
